"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import errorUtils from "@/helpers/error-utils";
import { cn } from "@/lib/utils";
import { logout } from "@/utils/apollo/apollo-wrapper";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

type LoginError = { field: string; message: string };

type User = {
  userRoles?: Array<{
    role: {
      slug: string;
    };
  }>;
};

const API_URL = process.env.NEXT_PUBLIC_API_URL;

// Helper function to check if user has admin role
function hasAdminRole(user: User): boolean {
  const userRoles = user?.userRoles || [];
  const adminRoles = [
    "SUPER_ADMIN",
    "ADMIN",
    "AUTHOR",
    "DIDACTIC_REVIEWER",
    "LANGUAGE_REVIEWER",
    "SUPERVISOR",
  ];
  return userRoles.some((ur: { role: { slug: string } }) =>
    adminRoles.includes(ur.role?.slug),
  );
}

export function StudentLoginForm({ className }: { className?: string }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<LoginError[]>([]);
  const [isChecking, setIsChecking] = useState(true);
  const router = useRouter();

  // Auto-logout if user has admin role
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const userStr = localStorage.getItem("user");

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        if (hasAdminRole(user)) {
          // Admin user trying to access student login - logout automatically
          logout();
        } else {
          // Student is already logged in — redirect to assessment
          router.replace("/assessment");
          return;
        }
      } catch {
        // Invalid user data, clear it
        logout();
      }
    }
    setIsChecking(false);
  }, [router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);

    const res = await fetch(`${API_URL}/graphql`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `
          mutation Login($input: LoginInput!) {
            login(input: $input) {
              ... on LoginPayload {
                access_token
                refresh_token
                expires_in
                user {
                    id
                    email
                    userRoles {
                        role {
                            slug
                        }
                    }
                }
              }
              ... on LoginErrorPayload {
                errors { field message }
              }
            }
          }
        `,
        variables: { input: { username, password } },
      }),
    });

    if (!res.ok) {
      setErrors([{ field: "_", message: `Chyba sítě: ${res.status}` }]);
      return;
    }

    const json = await res.json();
    const payload = json.data?.login;

    if (payload?.errors?.length) {
      // Translate error codes to Czech messages
      const translatedErrors = payload.errors.map((err: LoginError) => ({
        field: err.field,
        message:
          errorUtils.errorTexts[
            err.message as keyof typeof errorUtils.errorTexts
          ] || err.message,
      }));
      setErrors(translatedErrors);
      return;
    }

    if (payload?.access_token) {
      localStorage.setItem("access_token", payload.access_token);
      localStorage.setItem("refresh_token", payload.refresh_token);
      localStorage.setItem("expires_in", payload.expires_in);
      localStorage.setItem("user", JSON.stringify(payload.user));
      localStorage.setItem(
        "expires_at",
        (Date.now() + payload.expires_in * 1000).toString(),
      );

      // Set lightweight session cookie for Next.js middleware route protection
      document.cookie =
        "has_session=1; path=/; SameSite=Strict; max-age=86400";

      const userRoles = payload.user?.userRoles || [];
      const hasStudentRole = userRoles.some(
        (ur: { role: { slug: string } }) => ur.role?.slug === "STUDENT",
      );

      if (hasStudentRole) {
        router.replace("/assessment");
      } else {
        router.replace("/admin/tests");
      }
    } else {
      setErrors([{ field: "_", message: "Neznámá chyba přihlášení" }]);
    }
  };

  if (isChecking) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-white" />
      </div>
    );
  }

  return (
    <motion.div
      className={cn("flex flex-col gap-6", className)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <Card>
        <CardHeader className="text-center">
          <CardTitle>
            <div className="flex justify-center mb-4 items-center gap-2">
              <Image
                src="/theme/pangea/logo.svg"
                alt="Pangea Logo"
                width={50}
                height={50}
                priority
              />
            </div>
          </CardTitle>
          <CardDescription>
            <h1 className="text-2xl">Přihlášení studentů</h1>
            Přihlaste se svým kódem a heslem
          </CardDescription>
        </CardHeader>
        {errors.length > 0 && (
          <div className="px-6">
            <Alert variant="destructive">
              <AlertDescription>
                <span className="space-y-1">
                  {errors.map((err, i) => (
                    <span key={i}>{err.message}</span>
                  ))}
                </span>
              </AlertDescription>
            </Alert>
          </div>
        )}
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              <div className="grid gap-6">
                <div className="grid gap-2">
                  <Label htmlFor="code">Kód</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="123456"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Heslo</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                <Button type="submit" className="w-full">
                  Přihlásit se
                </Button>
              </div>
              <p className="text-center text-sm text-muted-foreground">
                Pokud máte technické problémy napište email na{" "}
                <a href="mailto:pangea@belcode.cz" className="underline">
                  pangea@belcode.cz
                </a>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  );
}
